import { useEffect, useState } from 'react';
import { Clock, Copy, Loader2, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { adminGetShareLink, adminCreateShareLink, adminRevokeShareLink } from '@/lib/api';
import { SHARE_LINK } from '@/constants/testIds';
import { fieldClass, goldButtonClass, labelClass, selectContentClass } from './styles';

const EXPIRY_OPTIONS = [
  { value: '24', label: '24 hours' },
  { value: '72', label: '3 days' },
  { value: '168', label: '7 days' },
  { value: '720', label: '30 days' },
];

/** Revocable, expiring, no-login preview link for a private gallery. */
export const ShareLinkDialog = ({ open, onOpenChange, slug }) => {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [expiryHours, setExpiryHours] = useState('168');

  useEffect(() => {
    if (!open || !slug) return;
    let cancelled = false;
    setLoading(true);
    setInfo(null);
    adminGetShareLink(slug)
      .then((d) => { if (!cancelled) setInfo(d); })
      .catch(() => { if (!cancelled) setInfo({ active: false }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, slug]);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await adminCreateShareLink(slug, Number(expiryHours));
      setInfo({ active: true, share_url: res.share_url, expires_at: res.expires_at });
      toast.success('Share link created.');
    } catch (err) {
      toast.error('Failed to create share link.');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    setLoading(true);
    try {
      await adminRevokeShareLink(slug);
      setInfo({ active: false });
      toast.success('Share link revoked.');
    } catch (err) {
      toast.error('Failed to revoke share link.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!info?.share_url) return;
    try {
      await navigator.clipboard.writeText(info.share_url);
      toast.success('Link copied to clipboard.');
    } catch (err) {
      toast.error('Could not copy the link.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={SHARE_LINK.dialog} className="bg-[#0F0F10] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[hsl(var(--lxra-fg))]">
            <Share2 className="h-5 w-5 text-[hsl(var(--lxra-gold))]" />
            Share Preview — {slug}
          </DialogTitle>
        </DialogHeader>
        {loading && !info && <Skeleton className="h-24 rounded-xl bg-[#141416]" />}
        {info && !info.active && (
          <div className="space-y-4">
            <p className="text-sm text-[hsl(var(--lxra-muted))]">
              Generate a view-only link anyone can open without logging in — perfect for a quick sneak peek. No favorites, selections, or comments happen through this link.
            </p>
            <div className="space-y-1.5">
              <Label className={labelClass}>Link expires in</Label>
              <Select value={expiryHours} onValueChange={setExpiryHours}>
                <SelectTrigger data-testid={SHARE_LINK.expirySelect} className={fieldClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={selectContentClass}>
                  {EXPIRY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleGenerate} disabled={loading} data-testid={SHARE_LINK.generateButton} className={`h-10 w-full ${goldButtonClass}`}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Generate Link'}
            </Button>
          </div>
        )}
        {info?.active && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-lg border border-[hsl(var(--lxra-border))] bg-[#141416] p-3">
              <p data-testid={SHARE_LINK.urlText} className="flex-1 text-xs text-[hsl(var(--lxra-fg))] truncate">{info.share_url}</p>
              <Button size="icon" variant="ghost" onClick={handleCopy} data-testid={SHARE_LINK.copyButton} className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] flex-shrink-0">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            {info.expires_at && (
              <p className="flex items-center gap-1.5 text-xs text-[hsl(var(--lxra-muted))]">
                <Clock className="h-3.5 w-3.5" /> Expires {new Date(info.expires_at).toLocaleString()}
              </p>
            )}
            <Button onClick={handleRevoke} disabled={loading} variant="outline" data-testid={SHARE_LINK.revokeButton} className="h-10 w-full rounded-lg border-red-400/40 text-red-400 hover:bg-red-400/10 text-xs uppercase tracking-[0.1em] font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Revoke Link'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
